import React, { useState } from "react";
import axios from "axios";
import { apiurl } from "../config/config";

export default function OrderLookup({ onOrderFetched }) {
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const fetchOrder = async () => {
    if (!orderId) return;

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const res = await axios.get(
        `${apiurl}/services/shopify/getShopifyOrderByNumber/${orderId}`
      );

      if (!res.data?.order) {
        setError("Order not found");
        onOrderFetched?.(null);
        return;
      }

      const o = res.data.order;

      // 🔥 NORMALIZED ORDER
      const normalizedOrder = {
        orderId: o.name,
        shopifyOrderId: o.id,
        orderDate: o.created_at,
        amount: o.current_total_price,
        paymentMode: o.payment_gateway_names?.join(", "),
        paymentStatus: o.financial_status,
        deliveryStatus: o.fulfillment_status,
        deliveryPartner: o.fulfillments?.[0]?.tracking_company,
        trackingId: o.fulfillments?.[0]?.tracking_number,
        trackingUrl: o.fulfillments?.[0]?.tracking_url,
        raw: o,
        customer: {
          name:
            o.billing_address?.name ||
            o.shipping_address?.name ||
            "",
          phone:
            o.billing_address?.phone ||
            o.shipping_address?.phone ||
            "",
          email: o.email || "",
        },
        shipping_address: o.shipping_address,
        billing_address: o.billing_address,
        line_items: o.line_items,
      };

      setOrder(normalizedOrder);

      // ✅ PASS DATA TO PARENT (CreateTicket)
      onOrderFetched?.(normalizedOrder);
    } catch (err) {
      setError("Order not found");
      onOrderFetched?.(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">

      {/* SEARCH */}
      <div className="flex gap-3">
        <input
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchOrder()}
          placeholder="Enter Order Number (e.g. 1000001)"
          className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
        <button
          onClick={fetchOrder}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Search
        </button>
      </div>

      {/* STATES */}
      {loading && <p className="text-gray-500">Fetching order…</p>}

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* ORDER PREVIEW */}
      {order && (
        <div className="space-y-8">

          {/* SUMMARY */}
          <section>
            <h3 className="font-semibold text-lg mb-3">Order Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Info label="Order No" value={order.orderId} />
              <Info label="Payment Status" value={order.paymentStatus} />
              <Info label="Amount" value={`₹${order.amount}`} />
              <Info
                label="Order Date"
                value={new Date(order.orderDate).toLocaleString()}
              />
            </div>
          </section>

          {/* CUSTOMER */}
          <section>
            <h3 className="font-semibold text-lg mb-3">Customer</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Info label="Name" value={order.customer.name} />
              {/* <Info label="Phone" value={order.customer.phone} /> */}
            </div>
          </section>

          {/* ADDRESSES */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Address title="Shipping Address" data={order.shipping_address} />
            <Address title="Billing Address" data={order.billing_address} />
          </section>

          {/* LINE ITEMS */}
          <section>
            <h3 className="font-semibold text-lg mb-3">Items</h3>
            <table className="w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">Product</th>
                  <th>SKU</th>
                  <th>Qty</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {order.line_items.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="p-3">{item.name}</td>
                    <td className="text-center">{item.sku}</td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-center">₹{item.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  );
}

/* ---------------- SMALL COMPONENTS ---------------- */

const Info = ({ label, value }) => (
  <div>
    <p className="text-gray-400">{label}</p>
    <p className="font-medium">{value || "-"}</p>
  </div>
);

const Address = ({ title, data }) => (
  <div className="border rounded-xl p-4 text-sm">
    <h4 className="font-medium mb-2">{title}</h4>
    {data ? (
      <p className="text-gray-600 leading-relaxed">
        {data.name}<br />
        {data.address1}<br />
        {data.city}, {data.province} {data.zip}<br />
        {data.country}<br />
        {/* 📞 {data.phone} */}
      </p>
    ) : (
      <p className="text-gray-400">Not available</p>
    )}
  </div>
);